import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ModernPricingSection } from '../components/ModernPricingSection';
import { api } from '../api';
import { 
    ArrowRight, 
    Check, 
    HelpCircle, 
    Users, 
    Clock,
    Zap,
    Shield,
    Star,
    ChevronDown,
    ChevronUp
} from 'lucide-react';

interface Tier {
    id: number;
    name: string;
    price: number;
    maxVodDuration: number;
    dailyVodUploadLimit: number;
    weeklyVodUploadLimit: number;
    monthlyVodUploadLimit: number;
    maxClipsPerVod: number;
    ffmpegResolution: string;
    ffmpegFps: number;
    editorResolution: string;
    editorFps: number;
    customAiPrompt: boolean;
    monthlyRedetectionLimit: number;
}

const FAQItem: React.FC<{
    question: string;
    answer: string;
    isOpen: boolean;
    onToggle: () => void;
}> = ({ question, answer, isOpen, onToggle }) => (
    <div className="border border-white/10 rounded-xl overflow-hidden bg-black/20 backdrop-blur-sm">
        <button
            className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-white/5 transition-colors"
            onClick={onToggle}
        >
            <span className="font-medium text-white">{question}</span>
            {isOpen ? (
                <ChevronUp size={20} className="text-white/60 flex-shrink-0" />
            ) : (
                <ChevronDown size={20} className="text-white/60 flex-shrink-0" />
            )}
        </button>
        {isOpen && (
            <div className="px-6 pb-4 border-t border-white/10">
                <p className="text-white/80 leading-relaxed">{answer}</p>
            </div>
        )}
    </div>
);

const PricingPage: React.FC = () => {
    const [tiers, setTiers] = useState<Tier[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [openFAQ, setOpenFAQ] = useState<number | null>(null);

    // Fetch tiers from API
    useEffect(() => {
        const fetchTiers = async () => {
            try {
                const response = await api.get('/tiers');
                if (response.data.success && response.data.tiers) {
                    setTiers(response.data.tiers);
                }
            } catch (error) {
                console.error('Error fetching tiers:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchTiers();
    }, []);

    const faqItems = [
        {
            question: "What is Clipoid?",
            answer: "Clipoid is an AI-powered platform that automatically extracts highlights and creates clips from your Twitch VODs. Our advanced AI analyzes your content to find the most engaging moments, saving you hours of manual editing."
        },
        {
            question: "How does the AI clip detection work?",
            answer: "Our AI uses advanced machine learning algorithms to analyze audio patterns, chat activity, and gameplay moments to identify highlights. It looks for peak excitement moments, funny interactions, and skillful plays to create engaging clips automatically."
        },
        {
            question: "Can I cancel my subscription anytime?",
            answer: "Yes, you can cancel your subscription at any time from your account settings. Your subscription will remain active until the end of your current billing period, and you'll retain access to all features until then."
        },
        {
            question: "What video quality do you support?",
            answer: "We support various video qualities depending on your plan. Free plans include 720p processing, Pro plans offer 1080p at 60fps, and Premium plans support up to 4K resolution for the highest quality clips."
        },
        {
            question: "What is your cancellation policy?",
            answer: "You can cancel your subscription at any time from your account settings. Your subscription will remain active until the end of your current billing period. Please note that we do not provide refunds for any payments made."
        },
        {
            question: "How long does clip processing take?",
            answer: "Processing time depends on your VOD length and selected plan. Typically, a 4-hour VOD takes 15-30 minutes to process. Premium subscribers get priority processing for faster turnaround times."
        },
        {
            question: "Can I use custom AI prompts?",
            answer: "Yes! Pro and Premium subscribers can create custom AI prompts to tailor clip detection to their specific content type, whether it's gaming, chatting, reactions, or educational content."
        },
        {
            question: "Is there an API available?",
            answer: "We're currently developing an API for enterprise customers. If you're interested in API access for automation or integration with your existing workflow, please contact our support team."
        }
    ];

    const features = [
        {
            icon: <Zap className="w-6 h-6" />,
            title: "AI-Powered Detection",
            description: "Advanced machine learning algorithms automatically identify your best moments"
        },
        {
            icon: <Clock className="w-6 h-6" />,
            title: "Save Hours of Work",
            description: "No more manual scrubbing through hours of footage to find highlights"
        },
        {
            icon: <Users className="w-6 h-6" />,
            title: "Multiple Platforms",
            description: "Support for Twitch, YouTube, and other streaming platforms"
        },
        {
            icon: <Shield className="w-6 h-6" />,
            title: "Secure & Private",
            description: "Your content is processed securely and never shared with third parties"
        }
    ];

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
            {/* Hero Section */}
            <div className="relative overflow-hidden">
                {/* Background Effects */}
                <div className="absolute inset-0">
                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-[800px] h-[800px] bg-gradient-to-r from-purple-500/20 to-blue-500/20 rounded-full blur-3xl" />
                </div>

                <div className="relative z-10 container mx-auto px-4 pt-16 pb-24">
                    <div className="text-center max-w-4xl mx-auto">
                        {/* Header Badge */}
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white/80 text-sm font-medium mb-6">
                            <Star size={16} className="text-yellow-400" />
                            Pricing Plans
                        </div>

                        <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
                            Choose Your
                            <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                                {" "}Perfect Plan
                            </span>
                        </h1>
                        
                        <p className="text-xl md:text-2xl text-white/70 mb-8 leading-relaxed max-w-3xl mx-auto">
                            Start free and scale up as your content grows. All plans include our powerful AI clip detection technology.
                        </p>

                        {/* Quick stats */}
                        <div className="flex flex-wrap justify-center gap-8 text-white/60 mb-12">
                            <div className="flex items-center gap-2">
                                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                                <span>No setup fees</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                                <span>Cancel anytime</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                                <span>Instant access</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Pricing Section */}
            <div className="container mx-auto px-4 py-16">
                {isLoading ? (
                    <div className="flex justify-center items-center h-64">
                        <div className="w-8 h-8 border-2 border-white/20 border-t-white/60 rounded-full animate-spin" />
                    </div>
                ) : (
                    <ModernPricingSection tiers={tiers} showSignUpButtons={true} compact={false} />
                )}
            </div>

            {/* Features Section */}
            <div className="container mx-auto px-4 py-16">
                <div className="text-center mb-16">
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                        Why Choose Clipoid?
                    </h2>
                    <p className="text-xl text-white/60 max-w-2xl mx-auto">
                        Built by content creators, for content creators. We understand what makes great clips.
                    </p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                    {features.map((feature, index) => (
                        <div key={index} className="text-center group">
                            <div className="
                                inline-flex items-center justify-center w-16 h-16 mb-4 rounded-2xl
                                bg-gradient-to-br from-purple-500/20 to-blue-500/20 border border-white/10
                                text-white group-hover:scale-110 transition-transform duration-200
                            ">
                                {feature.icon}
                            </div>
                            <h3 className="text-xl font-semibold text-white mb-2">
                                {feature.title}
                            </h3>
                            <p className="text-white/60 leading-relaxed">
                                {feature.description}
                            </p>
                        </div>
                    ))}
                </div>
            </div>

            {/* FAQ Section */}
            <div className="container mx-auto px-4 py-16">
                <div className="max-w-3xl mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                            Frequently Asked Questions
                        </h2>
                        <p className="text-xl text-white/60">
                            Got questions? We've got answers.
                        </p>
                    </div>

                    <div className="space-y-4">
                        {faqItems.map((item, index) => (
                            <FAQItem
                                key={index}
                                question={item.question}
                                answer={item.answer}
                                isOpen={openFAQ === index}
                                onToggle={() => setOpenFAQ(openFAQ === index ? null : index)}
                            />
                        ))}
                    </div>
                </div>
            </div>

            {/* CTA Section */}
            <div className="container mx-auto px-4 py-16">
                <div className="
                    max-w-4xl mx-auto text-center p-8 md:p-12 rounded-3xl
                    bg-gradient-to-r from-purple-500/20 to-blue-500/20 border border-white/10
                    backdrop-blur-sm
                ">
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                        Ready to Get Started?
                    </h2>
                    <p className="text-xl text-white/70 mb-8 max-w-2xl mx-auto">
                        Join thousands of content creators who are already using Clipoid to create amazing highlights automatically.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Link
                            to="/register"
                            className="
                                group inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl
                                bg-gradient-to-r from-purple-500 to-blue-500 text-white font-semibold text-lg
                                hover:from-purple-600 hover:to-blue-600 transition-all duration-200
                                shadow-lg hover:shadow-xl transform hover:scale-105
                            "
                        >
                            Start Free Trial
                            <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                        </Link>
                        <Link
                            to="/login"
                            className="
                                inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl
                                bg-white/10 border border-white/20 text-white font-semibold text-lg
                                hover:bg-white/15 hover:border-white/30 transition-all duration-200
                            "
                        >
                            Sign In
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PricingPage;